<script setup lang="ts">
import {computed, onMounted, PropType, ref, watch} from 'vue';
import {
    CabPeriod,
    CabReportItem,
    CabUser,
    OnderwerpEnum_name,
    PeriodEnum,
    renderCabPeriod,
    Role,
    StatusEnum,
    StatusEnum_name,
} from 'common';
import {ElCheckboxButton, ElCheckboxGroup, ElOption, ElSelect} from 'element-plus';
import {listObjects} from '@/base/callBackend.js';
import {localStorageGetJson, localStorageSetJson, SelectGroupInfo} from '@/base/utils.js';
import SelectorGroup from '@/views/shared/SelectorGroup.vue';
import WhiteCircleIcon from '@/views/icons/WhiteCircleIcon.vue';
import BlueCircleIcon from '@/views/icons/BlueCircleIcon.vue';
import PurpleCircleIcon from '@/views/icons/PurpleCircleIcon.vue';
import RedCircleIcon from '@/views/icons/RedCircleIcon.vue';
import GreenOrangeCircleIcon from '@/views/icons/GreenOrangeCircleIcon.vue';
import SelectAllIcon from '@/views/icons/SelectAllIcon.vue';
import SelectNoneIcon from '@/views/icons/SelectNoneIcon.vue';

const emit  = defineEmits(['update:filtered', 'update:statusFiltered']);
const props = defineProps({
    all: {
        type    : Array as PropType<CabReportItem[]>,
        required: true,
    },
    storageKeyPrefix: {
        type    : String,
        required: true,
    },
    showReporterFilter: {
        type   : Boolean,
        default: false,
    },
    showStatusFilter: {
        type   : Boolean,
        default: false,
    },
    /** true: plain item counts in blue, false: ready/todo counts in green/orange */
    isNotHeden: {
        type   : Boolean,
        default: false,
    },
});

const selectedKwartalenList    = ref<string[]>([]);
const selectedInstellingenList = ref<string[]>([]);
const selectedThemasList       = ref<string[]>([]);
const selectedOnderwerpenList  = ref<string[]>([]);
//
const ADMIN_FILTER_VALUE       = '';
const allReporters             = ref<CabUser[]>([]);
const selectedReporterUuid     = ref<string>(localStorageGetJson('admin:selectedReporter') ?? ADMIN_FILTER_VALUE);
//
const allStatuses: StatusEnum[] = [StatusEnum.overdue, StatusEnum.open, StatusEnum.submitted, StatusEnum.accepted, StatusEnum.rejected, StatusEnum.future];
const statusSet                 = ref<StatusEnum[]>(initStatusSet());
const currentKwartaal           = renderCabPeriod(CabPeriod.of(new Date(), PeriodEnum.Q));

// Persist reporter filter selection
watch(selectedReporterUuid, (newVal) => {
    localStorageSetJson('admin:selectedReporter', newVal);
});

// Status filtering is client-side - no backend reload needed
watch(statusSet, (newVal) => {
    localStorageSetJson(`${props.storageKeyPrefix}:selectedStatuses`, newVal);
});

function initStatusSet(): StatusEnum[] {
    const saved = localStorageGetJson(`${props.storageKeyPrefix}:selectedStatuses`);
    if (saved === undefined) {
        return [StatusEnum.open, StatusEnum.overdue];
    }
    return saved.filter((s: number) => allStatuses.includes(s));
}

onMounted(async () => {
    if (props.showReporterFilter) {
        const users        = await listObjects(CabUser);
        allReporters.value = users
            .filter(u => u.roles.includes(Role.reporter))
            .sort((a, b) => a.name.localeCompare(b.name));
    }
});

//============================================================================================================================================
const allNaStatus       = computed<CabReportItem[]>(() => {
    if (statusSet.value.length === 0) {
        return [];
    }
    const statusSetLookup = new Set(statusSet.value);
    return props.all.filter(ri => statusSetLookup.has(ri.status!));
});
const allNaReporter     = computed<CabReportItem[]>(() => {
    if (!props.showReporterFilter || selectedReporterUuid.value === ADMIN_FILTER_VALUE) {
        return allNaStatus.value;
    }
    const reporter = allReporters.value.find(r => r.uuid === selectedReporterUuid.value);
    if (!reporter) {
        return allNaStatus.value;
    }
    const orgThemeCombis = reporter.getOrgThemeCombiUuids();
    return allNaStatus.value.filter(ri => orgThemeCombis.some(([orgUuid, themeUuid]) =>
        ri.organization[0].uuid === orgUuid && ri.theme[0].uuid === themeUuid,
    ));
});
const allNaKwartalen    = computed<CabReportItem[]>(() => {
    const selectedSet = new Set(selectedKwartalenList.value);
    return allNaReporter.value.filter(ri => selectedSet.has(renderIndienMoment(ri)));
});
const allNaInstellingen = computed<CabReportItem[]>(() => {
    const selectedSet = new Set(selectedInstellingenList.value);
    return allNaKwartalen.value.filter(ri => selectedSet.has(ri.organization[0].uuid));
});
const allNaThemas       = computed<CabReportItem[]>(() => {
    const selectedSet = new Set(selectedThemasList.value);
    return allNaInstellingen.value.filter(ri => selectedSet.has(ri.theme[0].uuid));
});
const allNaOnderwerpen  = computed<CabReportItem[]>(() => {
    const selectedSet = new Set(selectedOnderwerpenList.value);
    return allNaThemas.value.filter(ri => selectedSet.has(ri.kpi[0].onderwerp?.toString() || ''));
});

watch(allNaStatus, newVal => emit('update:statusFiltered', newVal), {immediate: true});
watch(allNaOnderwerpen, newVal => emit('update:filtered', newVal), {immediate: true});

// Consolidated badge calculations - single pass through data for all maps
interface BadgeMaps {
    numAtKwartaal: Map<string, number>;
    numAtInstelling: Map<string, number>;
    numAtThema: Map<string, number>;
    numAtOnderwerp: Map<string, number>;
    numReadyAtKwartaal: Map<string, number>;
    numReadyAtInstelling: Map<string, number>;
    numReadyAtThema: Map<string, number>;
    numReadyAtOnderwerp: Map<string, number>;
    numTodoAtKwartaal: Map<string, number>;
    numTodoAtInstelling: Map<string, number>;
    numTodoAtThema: Map<string, number>;
    numTodoAtOnderwerp: Map<string, number>;
}

const badgeMaps = computed<BadgeMaps>(() => {
    const maps: BadgeMaps = {
        numAtKwartaal       : new Map(),
        numAtInstelling     : new Map(),
        numAtThema          : new Map(),
        numAtOnderwerp      : new Map(),
        numReadyAtKwartaal  : new Map(),
        numReadyAtInstelling: new Map(),
        numReadyAtThema     : new Map(),
        numReadyAtOnderwerp : new Map(),
        numTodoAtKwartaal   : new Map(),
        numTodoAtInstelling : new Map(),
        numTodoAtThema      : new Map(),
        numTodoAtOnderwerp  : new Map(),
    };

    // Pre-compute filter sets for efficiency
    const kwartaalSet   = new Set(selectedKwartalenList.value);
    const instellingSet = new Set(selectedInstellingenList.value);
    const themaSet      = new Set(selectedThemasList.value);

    // Single pass through allNaReporter for kwartaal maps
    for (const ri of allNaReporter.value) {
        const kwartaal = renderIndienMoment(ri);
        const isReady  = ri.isSubmittable();

        maps.numAtKwartaal.set(kwartaal, (maps.numAtKwartaal.get(kwartaal) || 0) + 1);
        if (isReady) {
            maps.numReadyAtKwartaal.set(kwartaal, (maps.numReadyAtKwartaal.get(kwartaal) || 0) + 1);
        } else {
            maps.numTodoAtKwartaal.set(kwartaal, (maps.numTodoAtKwartaal.get(kwartaal) || 0) + 1);
        }

        // Only process further if passes kwartaal filter
        if (!kwartaalSet.has(kwartaal)) {
            continue;
        }

        const instelling = ri.organization[0].uuid;
        maps.numAtInstelling.set(instelling, (maps.numAtInstelling.get(instelling) || 0) + 1);
        if (isReady) {
            maps.numReadyAtInstelling.set(instelling, (maps.numReadyAtInstelling.get(instelling) || 0) + 1);
        } else {
            maps.numTodoAtInstelling.set(instelling, (maps.numTodoAtInstelling.get(instelling) || 0) + 1);
        }

        // Only process further if passes instelling filter
        if (!instellingSet.has(instelling)) {
            continue;
        }

        const thema = ri.theme[0].uuid;
        maps.numAtThema.set(thema, (maps.numAtThema.get(thema) || 0) + 1);
        if (isReady) {
            maps.numReadyAtThema.set(thema, (maps.numReadyAtThema.get(thema) || 0) + 1);
        } else {
            maps.numTodoAtThema.set(thema, (maps.numTodoAtThema.get(thema) || 0) + 1);
        }

        // Only process further if passes thema filter
        if (!themaSet.has(thema)) {
            continue;
        }

        const onderwerp = ri.kpi[0].onderwerp?.toString() || '';
        maps.numAtOnderwerp.set(onderwerp, (maps.numAtOnderwerp.get(onderwerp) || 0) + 1);
        if (isReady) {
            maps.numReadyAtOnderwerp.set(onderwerp, (maps.numReadyAtOnderwerp.get(onderwerp) || 0) + 1);
        } else {
            maps.numTodoAtOnderwerp.set(onderwerp, (maps.numTodoAtOnderwerp.get(onderwerp) || 0) + 1);
        }
    }

    return maps;
});

//==================================================================================================
function kwartaalSelector(ri: CabReportItem): SelectGroupInfo {
    const x = renderIndienMoment(ri);
    return {
        name: x,
        id  : x,
    };
}

function instellingSelector(ri: CabReportItem): SelectGroupInfo {
    const x = ri.organization[0];
    return {
        name: x.name,
        id  : x.uuid,
    };
}

function themaSelector(ri: CabReportItem): SelectGroupInfo {
    const x = ri.theme[0];
    return {
        name: x.name,
        id  : x.uuid,
    };
}

function onderwerpSelector(ri: CabReportItem): SelectGroupInfo {
    const x = ri.kpi[0].onderwerp!;
    return {
        name: OnderwerpEnum_name(x),
        id  : x.toString(),
    };
}

//==================================================================================================
function getKwartaalBadgeValue(a: string): number | undefined {
    if (props.isNotHeden) {
        return badgeMaps.value.numAtKwartaal.get(a);
    } else {
        return badgeMaps.value.numReadyAtKwartaal.get(a) ?? (getKwartaalExtraBadgeValue(a) && 0);
    }
}

function getKwartaalExtraBadgeValue(a: string): number | undefined {
    if (props.isNotHeden) {
        return undefined;
    } else {
        return badgeMaps.value.numTodoAtKwartaal.get(a);
    }
}

function getInstellingBadgeValue(a: string): number | undefined {
    if (props.isNotHeden) {
        return badgeMaps.value.numAtInstelling.get(a);
    } else {
        return badgeMaps.value.numReadyAtInstelling.get(a) ?? (getInstellingExtraBadgeValue(a) && 0);
    }
}

function getInstellingExtraBadgeValue(a: string): number | undefined {
    if (props.isNotHeden) {
        return undefined;
    } else {
        return badgeMaps.value.numTodoAtInstelling.get(a);
    }
}

function getThemaBadgeValue(a: string): number | undefined {
    if (props.isNotHeden) {
        return badgeMaps.value.numAtThema.get(a);
    } else {
        return badgeMaps.value.numReadyAtThema.get(a) ?? (getThemaExtraBadgeValue(a) && 0);
    }
}

function getThemaExtraBadgeValue(a: string): number | undefined {
    if (props.isNotHeden) {
        return undefined;
    } else {
        return badgeMaps.value.numTodoAtThema.get(a);
    }
}

function getOnderwerpBadgeValue(a: string): number | undefined {
    if (props.isNotHeden) {
        return badgeMaps.value.numAtOnderwerp.get(a);
    } else {
        return badgeMaps.value.numReadyAtOnderwerp.get(a) ?? (getOnderwerpExtraBadgeValue(a) && 0);
    }
}

function getOnderwerpExtraBadgeValue(a: string): number | undefined {
    if (props.isNotHeden) {
        return undefined;
    } else {
        return badgeMaps.value.numTodoAtOnderwerp.get(a);
    }
}

//============================================================================================================================================
function renderIndienMoment(ri: CabReportItem) {
    let period: CabPeriod;
    if (ri.isMijlpaal) {
        period = CabPeriod.of(ri.meetMoment!, PeriodEnum.Q).previousPeriod();
    } else {
        period = ri.period[0].qPeriod();
    }
    return renderCabPeriod(period);
}

function getBadgeColor(): string {
    return props.isNotHeden ? 'blue' : 'green';
}

function getExtraBadgeColor(): string | undefined {
    return props.isNotHeden ? undefined : 'orange';
}

function getStatusFilterIcon(status: StatusEnum) {
    switch (status) {
        case StatusEnum.future:
            return WhiteCircleIcon;
        case StatusEnum.open:
        case StatusEnum.overdue:
            return GreenOrangeCircleIcon;
        case StatusEnum.submitted:
            return BlueCircleIcon;
        case StatusEnum.accepted:
            return PurpleCircleIcon;
        case StatusEnum.rejected:
            return RedCircleIcon;
    }
}
</script>

<template>
    <div v-if="showReporterFilter" class="flex flex-row px-10 w-full h-16">
        <div class="w-52 flex-shrink-0">
            <div class="pl-2 pr-4 py-2 my-1 text-right font-thin flex items-center justify-end gap-1">
                <span>reporter</span>
            </div>
        </div>
        <div class="flex-1 py-2">
            <el-select
                v-model="selectedReporterUuid"
                class="w-64"
                clearable
                placeholder="alle reporters"
            >
                <el-option
                    v-for="reporter in allReporters"
                    :key="reporter.uuid"
                    :value="reporter.uuid"
                    :label="reporter.name"
                />
            </el-select>
        </div>
    </div>
    <div v-if="showStatusFilter" class="flex flex-row px-10 w-full h-16">
        <div class="w-52 flex-shrink-0">
            <div class="pl-2 pr-4 py-2 my-1 text-right font-thin flex items-center justify-end gap-1">
                <span>statussen</span>
                <div class="flex flex-col">
                    <div
                        @click="statusSet = [...allStatuses]"
                        :class="statusSet.length === allStatuses.length ? 'cursor-default opacity-30' : 'cursor-pointer'"
                        title="Selecteer alles"
                    >
                        <SelectAllIcon/>
                    </div>
                    <div
                        @click="statusSet = []"
                        :class="statusSet.length === 0 ? 'cursor-default opacity-30' : 'cursor-pointer'"
                        title="Selecteer niets"
                    >
                        <SelectNoneIcon/>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex-1 py-2">
            <el-checkbox-group v-model="statusSet" class="flex">
                <el-checkbox-button
                    v-for="status in allStatuses"
                    :key="status"
                    :value="status"
                    border
                >
                    <span class="flex items-center gap-1">
                        <component :is="getStatusFilterIcon(status)"/>
                        {{ StatusEnum_name(status) }}
                    </span>
                </el-checkbox-button>
            </el-checkbox-group>
        </div>
    </div>
    <SelectorGroup
        :all="allNaReporter"
        :names="['kwartaal','kwartalen']"
        :local-storage-name="`${storageKeyPrefix}:selectedKwartalen`"
        :select-field="kwartaalSelector"
        v-model:selected-list="selectedKwartalenList"
        :get-badge-value="getKwartaalBadgeValue"
        :get-extra-badge-value="getKwartaalExtraBadgeValue"
        :badge-color="getBadgeColor()"
        :extra-badge-color="getExtraBadgeColor()"
        :highlight-id="currentKwartaal"
    />
    <SelectorGroup
        :all="allNaReporter"
        :names="['instelling','instellingen']"
        :local-storage-name="`${storageKeyPrefix}:selectedInstellingen`"
        :select-field="instellingSelector"
        v-model:selected-list="selectedInstellingenList"
        :get-badge-value="getInstellingBadgeValue"
        :get-extra-badge-value="getInstellingExtraBadgeValue"
        :badge-color="getBadgeColor()"
        :extra-badge-color="getExtraBadgeColor()"
    />
    <SelectorGroup
        :all="allNaReporter"
        :names="['thema','themas']"
        :local-storage-name="`${storageKeyPrefix}:selectedThemas`"
        :select-field="themaSelector"
        v-model:selected-list="selectedThemasList"
        :get-badge-value="getThemaBadgeValue"
        :get-extra-badge-value="getThemaExtraBadgeValue"
        :badge-color="getBadgeColor()"
        :extra-badge-color="getExtraBadgeColor()"
    />
    <SelectorGroup
        :all="allNaReporter"
        :names="['onderwerp','onderwerpen']"
        :local-storage-name="`${storageKeyPrefix}:selectedOnderwerpen`"
        :select-field="onderwerpSelector"
        v-model:selected-list="selectedOnderwerpenList"
        :get-badge-value="getOnderwerpBadgeValue"
        :get-extra-badge-value="getOnderwerpExtraBadgeValue"
        :badge-color="getBadgeColor()"
        :extra-badge-color="getExtraBadgeColor()"
    />
</template>
